import { useCallback, useEffect, useRef } from 'react'

import { SESSION_CONFIG } from '../config/session.config'

import { resetAuthStore, setAuthInfo } from '@v2/domains/auth/store/auth.store'
import { useAppDispatch } from '@v2/store/hooks'

export type TabSyncEvent =
  | { type: 'ACTIVITY'; timestamp: number }
  | { type: 'LOGOUT'; timestamp: number }
  | {
      type: 'TOKEN_REFRESH'
      accessToken?: string
      refreshToken?: string
      timestamp: number
    }
  | { type: 'SHOW_MODAL'; timestamp: number }
  | { type: 'HIDE_MODAL'; timestamp: number }
  | { type: 'RESET_TIMER'; timestamp: number }
  | {
      type: 'ORG_SWITCH'
      eventId: string
      sourceTabId: string
      organizationId: string
      organizationName: string
      targetPath: string
      timestamp: number
    }

export type TabSyncEventInput =
  | { type: 'ACTIVITY' }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_REFRESH'; accessToken?: string; refreshToken?: string }
  | { type: 'SHOW_MODAL' }
  | { type: 'HIDE_MODAL' }
  | { type: 'RESET_TIMER' }
  | {
      type: 'ORG_SWITCH'
      organizationId: string
      organizationName: string
      targetPath: string
    }

const CHANNEL_NAME = 'w360-session-sync'
export const ORGANIZATION_SWITCH_STORAGE_KEY = 'w360-organization-switch-event'
export const LAST_HANDLED_ORGANIZATION_SWITCH_EVENT_STORAGE_KEY =
  'w360-organization-switch-last-handled-event'
const TAB_CREATED_AT_STORAGE_KEY = 'w360-organization-switch-tab-created-at'
let documentTabId: string | null = null
let documentTabCreatedAt: number | null = null

export type OrganizationSwitchTabSyncEvent = Extract<
  TabSyncEvent,
  { type: 'ORG_SWITCH' }
>

interface UseTabSyncOptions {
  onResetTimer?: () => void
  onShowModal?: () => void
  onHideModal?: () => void
  onLogout?: () => void
  onTokenRefresh?: () => void
  onOrgSwitch?: (event: OrganizationSwitchTabSyncEvent) => void
}

const createUniqueId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

export const getTabSyncTabId = () => {
  if (!documentTabId) {
    // Duplicating a tab can clone sessionStorage, so its ID must live in memory.
    documentTabId = `tab-${createUniqueId()}`
  }

  return documentTabId
}

export const getTabSyncCreatedAt = () => {
  try {
    const existingCreatedAt = Number(
      sessionStorage.getItem(TAB_CREATED_AT_STORAGE_KEY),
    )

    if (Number.isFinite(existingCreatedAt) && existingCreatedAt > 0) {
      documentTabCreatedAt = existingCreatedAt
      return existingCreatedAt
    }

    documentTabCreatedAt ??= Date.now()
    sessionStorage.setItem(
      TAB_CREATED_AT_STORAGE_KEY,
      String(documentTabCreatedAt),
    )
  } catch {
    documentTabCreatedAt ??= Date.now()
  }
  return documentTabCreatedAt
}

export const isOrganizationSwitchEvent = (
  value: unknown,
): value is OrganizationSwitchTabSyncEvent => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const event = value as Partial<OrganizationSwitchTabSyncEvent>

  return (
    event.type === 'ORG_SWITCH' &&
    typeof event.eventId === 'string' &&
    typeof event.sourceTabId === 'string' &&
    typeof event.organizationId === 'string' &&
    event.organizationId.length > 0 &&
    typeof event.organizationName === 'string' &&
    event.organizationName.length > 0 &&
    typeof event.targetPath === 'string' &&
    event.targetPath.startsWith('/') &&
    !event.targetPath.startsWith('//') &&
    typeof event.timestamp === 'number'
  )
}

export const getLatestOrganizationSwitchEvent = () => {
  let serializedEvent: string | null = null

  try {
    serializedEvent = localStorage.getItem(ORGANIZATION_SWITCH_STORAGE_KEY)
  } catch {
    return null
  }

  if (!serializedEvent) {
    return null
  }

  try {
    const event = JSON.parse(serializedEvent)
    return isOrganizationSwitchEvent(event) ? event : null
  } catch {
    return null
  }
}

export const useTabSync = (options?: UseTabSyncOptions) => {
  const dispatch = useAppDispatch()
  const channelRef = useRef<BroadcastChannel | null>(null)
  const optionsRef = useRef(options)
  const processedOrganizationEventsRef = useRef(new Set<string>())

  useEffect(() => {
    optionsRef.current = options
  }, [options])

  useEffect(() => {
    const handleEvent = (data: TabSyncEvent) => {
      if (data.type === 'ORG_SWITCH') {
        if (processedOrganizationEventsRef.current.has(data.eventId)) {
          return
        }

        processedOrganizationEventsRef.current.add(data.eventId)
        optionsRef.current?.onOrgSwitch?.(data)
        return
      }

      if (Date.now() - data.timestamp > 5000) {
        return
      }

      switch (data.type) {
        case 'ACTIVITY':
          localStorage.setItem(
            SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
            String(data.timestamp),
          )
          optionsRef.current?.onResetTimer?.()
          break

        case 'LOGOUT':
          dispatch(resetAuthStore())
          optionsRef.current?.onLogout?.()

          setTimeout(() => {
            window.location.href = '/login'
          }, 100)
          break

        case 'TOKEN_REFRESH':
          if (data.accessToken && data.refreshToken) {
            dispatch(
              setAuthInfo({
                access_token: data.accessToken,
                refresh_token: data.refreshToken,
              }),
            )
          }
          optionsRef.current?.onTokenRefresh?.()
          break

        case 'SHOW_MODAL':
          optionsRef.current?.onShowModal?.()
          break

        case 'HIDE_MODAL':
          optionsRef.current?.onHideModal?.()
          break

        case 'RESET_TIMER':
          optionsRef.current?.onResetTimer?.()
          break
      }
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== ORGANIZATION_SWITCH_STORAGE_KEY || !event.newValue) {
        return
      }

      try {
        const organizationSwitchEvent = JSON.parse(event.newValue)

        if (isOrganizationSwitchEvent(organizationSwitchEvent)) {
          handleEvent(organizationSwitchEvent)
        }
      } catch {
        // Ignore malformed cross-tab data rather than breaking session sync.
      }
    }

    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel(CHANNEL_NAME)
      channelRef.current = channel
      channel.onmessage = (event: MessageEvent<TabSyncEvent>) => {
        handleEvent(event.data)
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      channelRef.current?.close()
      channelRef.current = null
      window.removeEventListener('storage', handleStorage)
    }
  }, [dispatch])

  const broadcast = useCallback((event: TabSyncEventInput) => {
    const timestamp = Date.now()
    const eventWithTimestamp: TabSyncEvent =
      event.type === 'ORG_SWITCH'
        ? {
            ...event,
            eventId: createUniqueId(),
            sourceTabId: getTabSyncTabId(),
            timestamp,
          }
        : ({ ...event, timestamp } as TabSyncEvent)

    if (eventWithTimestamp.type === 'ORG_SWITCH') {
      try {
        sessionStorage.setItem(
          LAST_HANDLED_ORGANIZATION_SWITCH_EVENT_STORAGE_KEY,
          eventWithTimestamp.eventId,
        )
      } catch {
        // The current document still recognizes its own event by sourceTabId.
      }

      try {
        localStorage.setItem(
          ORGANIZATION_SWITCH_STORAGE_KEY,
          JSON.stringify(eventWithTimestamp),
        )
      } catch {
        // BroadcastChannel still provides immediate delivery when storage fails.
      }
    }

    channelRef.current?.postMessage(eventWithTimestamp)
  }, [])

  const updateActivity = useCallback(() => {
    const timestamp = Date.now()
    localStorage.setItem(
      SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
      String(timestamp),
    )
    broadcast({ type: 'ACTIVITY' })
  }, [broadcast])

  return {
    broadcast,
    updateActivity,
  }
}
